import { useMemo, useCallback } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation } from '../../hooks';
import { queryCache } from '../../cache';
import { TherapeuticAreaService } from './service';
import {
  TherapeuticArea,
  CreateTherapeuticAreaRequest,
  UpdateTherapeuticAreaRequest,
  TherapeuticAreaQueryParams,
  TherapeuticAreaQueryResponse,
} from './types';
import {
  useMergeSuggestions as useGenericMergeSuggestions,
  useMergePreview as useGenericMergePreview,
  useMerge as useGenericMerge,
} from '../merge/hooks';
import { THERAPEUTIC_AREAS_MERGE_CONFIG } from '../merge/config';

const CACHE_KEY = 'therapeutic-areas';

export function useTherapeuticAreas(params?: TherapeuticAreaQueryParams) {
  const api = useApi();
  const service = useMemo(() => new TherapeuticAreaService(api), [api]);
  const fetcher = useCallback(() => service.getAll(params), [service, params]);

  return useQuery<TherapeuticAreaQueryResponse>(CACHE_KEY, fetcher);
}

export function useTherapeuticArea(id: number) {
  const api = useApi();
  const service = useMemo(() => new TherapeuticAreaService(api), [api]);
  const fetcher = useCallback(() => service.getById(id), [service, id]);

  return useQuery<TherapeuticArea>(`${CACHE_KEY}/${id}`, fetcher);
}

export function useCreateTherapeuticArea() {
  const api = useApi();
  const service = useMemo(() => new TherapeuticAreaService(api), [api]);

  return useMutation<TherapeuticArea, CreateTherapeuticAreaRequest>(
    data => service.create(data),
    {
      onSuccess: () => {
        queryCache.invalidate(CACHE_KEY);
      },
    }
  );
}

export function useUpdateTherapeuticArea() {
  const api = useApi();
  const service = useMemo(() => new TherapeuticAreaService(api), [api]);

  return useMutation<TherapeuticArea, UpdateTherapeuticAreaRequest>(
    data => service.update(data),
    {
      onSuccess: data => {
        queryCache.invalidate(CACHE_KEY);
        queryCache.invalidate(`${CACHE_KEY}/${data.therapeutic_area_id}`);
      },
    }
  );
}

export function useDeleteTherapeuticArea() {
  const api = useApi();
  const service = useMemo(() => new TherapeuticAreaService(api), [api]);

  return useMutation<void, number>(id => service.delete(id), {
    onSuccess: (_, id) => {
      queryCache.invalidate(CACHE_KEY);
      queryCache.invalidate(`${CACHE_KEY}/${id}`);
    },
  });
}

// Convenience hook that combines all therapeutic area operations
export function useTherapeuticAreasPage(params?: TherapeuticAreaQueryParams) {
  const therapeuticAreaResponse = useTherapeuticAreas(params);
  const createTherapeuticArea = useCreateTherapeuticArea();
  const updateTherapeuticArea = useUpdateTherapeuticArea();
  const deleteTherapeuticArea = useDeleteTherapeuticArea();

  return useMemo(
    () => ({
      // Query operations
      therapeuticAreas: therapeuticAreaResponse,

      // Mutation operations
      create: createTherapeuticArea,
      update: updateTherapeuticArea,
      delete: deleteTherapeuticArea,

      // Convenience getters for common states
      data:
        therapeuticAreaResponse.status === 'success'
          ? (therapeuticAreaResponse.data?.therapeutic_areas ?? [])
          : [],
      total:
        therapeuticAreaResponse.status === 'success'
          ? (therapeuticAreaResponse.data?.total ?? 0)
          : 0,
      isLoading: therapeuticAreaResponse.status === 'loading',
      isError: therapeuticAreaResponse.status === 'error',
      error:
        therapeuticAreaResponse.status === 'error'
          ? therapeuticAreaResponse.error
          : null,

      // Actions
      refetch: therapeuticAreaResponse.refetch,
      invalidate: therapeuticAreaResponse.invalidate,
    }),
    [
      therapeuticAreaResponse,
      createTherapeuticArea,
      updateTherapeuticArea,
      deleteTherapeuticArea,
    ]
  );
}

// ============================================================================
// MERGE HOOKS - Using generic merge functionality
// ============================================================================

/**
 * Get merge suggestions for a therapeutic area
 */
export function useMergeSuggestions(sourceId: number | null, maxResults = 10) {
  return useGenericMergeSuggestions<TherapeuticArea>(
    THERAPEUTIC_AREAS_MERGE_CONFIG,
    sourceId,
    maxResults
  );
}

/**
 * Get merge preview showing impact of merging two therapeutic areas
 */
export function useMergePreview(
  sourceId: number | null,
  targetId: number | null
) {
  return useGenericMergePreview(
    THERAPEUTIC_AREAS_MERGE_CONFIG,
    sourceId,
    targetId
  );
}

/**
 * Execute a therapeutic area merge operation
 */
export function useMergeTherapeuticAreas() {
  return useGenericMerge(THERAPEUTIC_AREAS_MERGE_CONFIG, CACHE_KEY);
}
